import * as ProgressPrimitive from '@radix-ui/react-progress';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../../../lib/utils';

export interface ProgressProps extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Extra classes for the filled indicator (e.g. a tone colour); defaults to `bg-primary`. */
  indicatorClassName?: string;
}

/**
 * Linear progress (0–100). Uses Radix Progress for semantics; indicator width follows `value`.
 */
const Progress = forwardRef<ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  ({ className, indicatorClassName, value, max = 100, ...props }, ref) => {
    const pct =
      max > 0 && value != null
        ? Math.min(100, Math.max(0, (Number(value) / Number(max)) * 100))
        : 0;
    return (
      <ProgressPrimitive.Root
        ref={ref}
        data-slot="progress"
        className={cn('relative h-2 w-full overflow-hidden rounded-full bg-secondary', className)}
        value={value}
        max={max}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            'h-full w-full flex-1 rounded-full bg-primary transition-all',
            indicatorClassName
          )}
          style={{ transform: `translateX(-${100 - pct}%)` }}
        />
      </ProgressPrimitive.Root>
    );
  }
);
Progress.displayName = ProgressPrimitive.Root.displayName ?? 'Progress';

export { Progress };
